/**
 * `DataTableBodyRow` — one rendered BODY row: the model-external selection cell, every visible
 * data cell on its column track, the `renderRow`/`renderCell` seams, and the master-detail panel
 * that follows an expanded row. Split out of `data-table.tsx` for the same reason as
 * `DataTableHeaderRow` and `DataTableSummaryBand` — the orchestrator holds the row as a single
 * node instead of a third cell-derivation block.
 *
 * REACTIVITY: the cell tracks are derived HERE, in this component's own render, off the
 * referentially-stable `table`; `pinnedOffsets` is the carrier that keeps them live — the
 * preset memoizes that map on exactly the pin/size/order/visibility state slices, so a new
 * identity arrives precisely when the geometry changes and NOT otherwise.
 *
 * MEMOIZED, deliberately: every prop is a primitive, a preset-stable reference, or a
 * consumer seam the consumer is expected to keep stable (`getRowProps` / `renderRow` /
 * `renderCell` — see `DataTableProps`). A selection toggle then re-renders only the rows
 * whose `selected` flipped instead of every mounted row — a measured ~200ms of row
 * re-renders per header-checkbox click on the 14-column hands grid before this (FE-836 QA
 * round 13). The windowed per-slot `style` is identity-cached by the preset per slot; grid
 * nav arrives as the `gridId`/`activeCellId` PRIMITIVES (ids derive through the pure
 * `buildGridCellId`), not the per-render `gridNav` object.
 */
import type { CSSProperties, ReactNode } from 'react'
import { flexRender, type Row, type RowData } from '@tanstack/react-table'

import type {
  DataTableCellRenderContext,
  DataTableDetailPanel,
  DataTableProps,
  DataTableRowRenderContext,
} from '../data-table.types'
import { getCellTrackProps } from './column-size'
import type { DataTableDensity, DataTableDivider, DataTableSurface } from './data-table.styles'
import { genericMemo } from './generic-memo'
import type { PinnedOffsetMap } from './pinning.styles'
import {
  SELECTION_COLUMN_WIDTH_PX,
  TableCell,
  TableRow,
  type TableRowProps,
  TableSelectionCell,
} from './table-parts'
import { buildGridCellId } from './use-grid-keyboard-nav'

export type DataTableBodyRowProps<TData extends RowData> = {
  /**
   * The preset's `columns` prop, held ONLY as a memo identity key (never read): a consumer
   * that rebuilds its column defs per render — cell closures over component state, the
   * TanStack-sanctioned pattern (e.g. an inline delete-confirm flow) — must punch through
   * this memo even when `row`/`data` identities did not change, or the old closures render
   * a frozen UI. A consumer with stable columns keeps the full bailout.
   */
  columnDefsIdentity: DataTableProps<TData>['columns']
  row: Row<TData>
  /**
   * The row's position in the DISPLAY-ordered `rows` array — the logical space the U4 index map
   * is built from — NOT TanStack's `row.index` (the pre-sort/pre-filter core position, which the
   * sorted/pagination models do NOT renumber). Feeding `row.index` makes a sorted or page-≥2 row
   * announce its original data position, so `aria-rowindex` runs non-monotonic against reading
   * order. Windowed passes `virtualItem.index`; non-windowed the `.map` index.
   */
  displayIndex: number
  /** Absolute slot placement in the windowed tiers; omitted when rows render in flow. */
  style?: CSSProperties
  ariaRowIndex: number
  density: DataTableDensity
  divider: DataTableDivider
  /**
   * Whether this row occupies the body's FINAL slot — derived by the preset, NOT from DOM
   * position, because a windowed body mounts a moving slice (see `TableRow['last']`). A
   * primitive, like `selected`, so a row re-renders on it alone.
   */
  lastRow: boolean
  /** Whether the body uses grow columns, which binds every plane to the scrollport. */
  fitScrollport: boolean
  /** The card's surface — the opaque background a pinned body cell paints. */
  surface: DataTableSurface
  pinnedOffsets: PinnedOffsetMap
  /** The grid-nav id namespace — cell ids derive via `buildGridCellId` (see header). */
  gridId: string
  /** The active cell's DOM id, or `undefined` — the row's cells compare their own ids to it. */
  activeCellId: string | undefined
  /**
   * Accessible name for the row checkbox; `undefined` ⇔ the table has no selection column.
   * A primitive on purpose — handing rows the whole `DataTableSelection` object (fresh per
   * consumer render) would defeat the memo above.
   */
  selectRowLabel: string | undefined
  /** Whether a selected row paints its background highlight (`selection.rowHighlight`). */
  rowHighlightOnSelect: boolean
  /**
   * Whether THIS row is selected — derived by the preset, not here. A boolean rather than the
   * whole `SelectionModel`: a selection change then flips one prop on the two rows it actually
   * touched instead of handing every mounted row a new model to re-derive itself from. It also
   * folds in the keepPreviousData guard (stale rows about to be replaced never render as
   * selected, matching the header, which feeds `loadedCount: 0`).
   */
  selected: boolean
  selectionCellClassName: string | undefined
  selectionPinnedStyle: CSSProperties | undefined
  onToggleRowSelection: (rowId: string, next: boolean) => void
  detailPanel: DataTableDetailPanel<TData> | undefined
  /** Per-table namespace for the panels' DOM ids, so two tables never cross-wire. */
  detailPanelBaseId: string
  getRowProps?: (row: Row<TData>) => Partial<TableRowProps>
  renderCell?: (ctx: DataTableCellRenderContext<TData>) => ReactNode
  renderRow?: (ctx: DataTableRowRenderContext<TData>) => ReactNode
}

type DataTableRowCellsProps<TData extends RowData> = {
  /**
   * The preset's `columns` prop, held ONLY as a memo identity key (never read): a consumer
   * that rebuilds its column defs per render — cell closures over component state, the
   * TanStack-sanctioned pattern (e.g. an inline delete-confirm flow) — must punch through
   * this memo even when `row`/`data` identities did not change, or the old closures render
   * a frozen UI. A consumer with stable columns keeps the full bailout.
   */
  columnDefsIdentity: DataTableProps<TData>['columns']
  row: Row<TData>
  displayIndex: number
  dataColOffset: number
  gridId: string
  activeCellId: string | undefined
  pinnedOffsets: PinnedOffsetMap
  surface: DataTableSurface
  renderCell?: (ctx: DataTableCellRenderContext<TData>) => ReactNode
}

/**
 * The row's DATA cells as their own memo boundary: none of their inputs involve
 * `selected`, so a selection change — including select-all, which flips `selected` on
 * EVERY row — re-renders only the row shells and their checkboxes while the 14-odd
 * heavy cell subtrees (poker hands, boards, per-street icons) bail. This is the bulk
 * of the row's render cost (FE-836 QA round 13).
 */
const DataTableRowCellsImpl = <TData extends RowData>({
  row,
  displayIndex,
  dataColOffset,
  gridId,
  activeCellId,
  pinnedOffsets,
  surface,
  renderCell,
}: DataTableRowCellsProps<TData>) => (
  <>
    {row.getVisibleCells().map((cell, colIndex) => {
      const cellDefaultContent = flexRender(cell.column.columnDef.cell, cell.getContext())
      const content =
        renderCell?.({ cell, defaultContent: cellDefaultContent }) ?? cellDefaultContent
      // 0-based visible-column nav space (selection column is 0); `aria-colindex` is
      // this coordinate + 1, keeping the cell's `id` / `active` flag aligned with it.
      const cellId = buildGridCellId(gridId, {
        rowIndex: displayIndex,
        colIndex: colIndex + dataColOffset,
      })

      return (
        <TableCell
          key={cell.id}
          active={cellId === activeCellId}
          colIndex={colIndex + 1 + dataColOffset}
          id={cellId}
          {...getCellTrackProps(cell, pinnedOffsets, surface)}
        >
          {content}
        </TableCell>
      )
    })}
  </>
)

const DataTableRowCells = genericMemo(DataTableRowCellsImpl)

/**
 * Plain generic function (not `FC`) so `Row<TData>` inference survives — same reason as the
 * header row / summary band.
 */
const DataTableBodyRowImpl = <TData extends RowData>({
  columnDefsIdentity,
  row,
  displayIndex,
  style,
  ariaRowIndex,
  density,
  divider,
  lastRow,
  fitScrollport,
  surface,
  pinnedOffsets,
  gridId,
  activeCellId,
  selectRowLabel,
  rowHighlightOnSelect,
  selected,
  selectionCellClassName,
  selectionPinnedStyle,
  onToggleRowSelection,
  detailPanel,
  detailPanelBaseId,
  getRowProps,
  renderCell,
  renderRow,
}: DataTableBodyRowProps<TData>) => {
  const extraRowProps = getRowProps?.(row)
  // The selection column, when present, occupies visible index 1, so data cells'
  // `aria-colindex` shifts by one to stay contiguous with the selection header (U6).
  const hasSelectionColumn = selectRowLabel !== undefined
  const dataColOffset = hasSelectionColumn ? 1 : 0
  // Master-detail: the row-click toggle composes AFTER a caller `getRowProps` onClick
  // (both run); in-cell content that must not toggle stops propagation, as the
  // selection cell already does, and the caller's handler can veto a specific
  // click's toggle via `preventDefault()`.
  const isExpanded = detailPanel !== undefined && row.id === detailPanel.expandedRowId
  const panelId = `${detailPanelBaseId}-${row.id}`
  const handleRowClick: TableRowProps['onClick'] | undefined =
    detailPanel === undefined
      ? undefined
      : (event) => {
          extraRowProps?.onClick?.(event)
          if (!event.defaultPrevented) {
            detailPanel.onExpandedRowIdChange(isExpanded ? undefined : row.id)
          }
        }
  // Row-background highlight is opt-out (`selection.rowHighlight: false`) — the checkbox
  // still reflects `selected`, only the row's selected styling is suppressed.
  const rowHighlighted = selected && rowHighlightOnSelect

  const defaultRowProps: TableRowProps = {
    density,
    divider,
    // An EXPANDED last row is not the body's bottom edge — its detail panel is, and the panel
    // draws its own `border-b`. Suppressing here would erase the line between the row and its
    // own panel (and is what `:last-child` did too, since the panel took that slot).
    //
    // Residue, pre-existing and deliberately not fixed here: the panel's `border-b` below is
    // UNCONDITIONAL, so an expanded LAST row still doubles the card's bottom edge. Handing the
    // panel the flag is the fix; it is a separate change, and this carve-out is what keeps the
    // current behaviour unchanged rather than trading one doubled edge for a missing separator.
    last: lastRow && !isExpanded,
    fitScrollport,
    rowIndex: ariaRowIndex,
    selected: rowHighlighted,
    style,
    ...(detailPanel === undefined ? {} : { interactive: true }),
    ...extraRowProps,
    // The DS wins `aria-expanded`/`aria-controls`/`onClick` over a colliding
    // `getRowProps` value (the caller's onClick is composed in, not dropped);
    // `interactive` above stays caller-overridable like any other row prop.
    ...(detailPanel === undefined
      ? {}
      : {
          'aria-expanded': isExpanded,
          ...(isExpanded ? { 'aria-controls': panelId } : {}),
        }),
    ...(handleRowClick === undefined ? {} : { onClick: handleRowClick }),
  }
  const defaultContent = (
    <>
      {hasSelectionColumn && (
        <TableSelectionCell
          active={buildGridCellId(gridId, { rowIndex: displayIndex, colIndex: 0 }) === activeCellId}
          aria-label={selectRowLabel}
          checked={selected}
          className={selectionCellClassName}
          colIndex={1}
          id={buildGridCellId(gridId, { rowIndex: displayIndex, colIndex: 0 })}
          style={{ width: SELECTION_COLUMN_WIDTH_PX, ...selectionPinnedStyle }}
          onCheckedChange={(next) => onToggleRowSelection(row.id, next)}
        />
      )}
      <DataTableRowCells<TData>
        activeCellId={activeCellId}
        columnDefsIdentity={columnDefsIdentity}
        dataColOffset={dataColOffset}
        displayIndex={displayIndex}
        gridId={gridId}
        pinnedOffsets={pinnedOffsets}
        renderCell={renderCell}
        row={row}
        surface={surface}
      />
    </>
  )
  const renderedRow = renderRow?.({ row, defaultContent, defaultRowProps }) ?? (
    <TableRow {...defaultRowProps}>{defaultContent}</TableRow>
  )

  return (
    <>
      {renderedRow}
      {detailPanel !== undefined && isExpanded && (
        // A plain `role="row"` div, NOT `TableRow` — that would clamp the panel to the
        // density's inline row height (see `DataTableDetailPanel`). It shares the master
        // row's `aria-rowindex` rather than claiming its own: `aria-rowcount` counts
        // engine rows only, so a separate number would announce one more row than exists.
        // `data-detail-panel` lets a consumer's row-scoped selectors exclude it.
        <div
          data-detail-panel
          aria-rowindex={defaultRowProps.rowIndex}
          className="w-full border-b border-divider bg-paper-elevation3"
          id={panelId}
          role="row"
        >
          <div
            aria-colindex={1}
            // +offset: the selection column renders outside the TanStack model.
            aria-colspan={row.getVisibleCells().length + dataColOffset}
            className="w-full"
            role="gridcell"
          >
            {detailPanel.render(row)}
          </div>
        </div>
      )}
    </>
  )
}

export const DataTableBodyRow = genericMemo(DataTableBodyRowImpl)
